import { createSlice, current } from '@reduxjs/toolkit';
import _ from 'lodash';
import { SearchStatus, FiltersInitialValue } from '../../constants';

const initialState = {
  hotelSearches: {},
  hotelSearchResponses: {},
  hotels: {},
  hotelDetails: {},
  hotelDetailResponses: {},
  getRoomDetailsStatus: {},
  roomDescriptions: {},
  error: {},
  hasUserInteractedWithHotelList: false,
  hotelRateDescription: undefined,
  hotelRateCodeKeysLoadingStatus: SearchStatus.INITIAL,
  deepLinkSearchStatus: SearchStatus.INITIAL,
  isHotelDetailLoading: false,
  isHotelRateSearch: false,
  currentRateDetailCodes: {},
};
const hotelSlice = createSlice({
  name: 'hotel',
  initialState,
  reducers: {
    updateHotelSearchObj: (state, {
      payload: {
        tripId, searchObj, defaultSearchObj, searchStatus, selectedAddress,
        searchProgress, requestId,
      },
    }) => ({
      ...state,
      hotelSearches: {
        ...state.hotelSearches,
        ...{
          [tripId]: {
            ...state.hotelSearches[tripId],
            searchStatus: searchStatus === undefined || searchStatus === null
              ? ((state.hotelSearches[tripId] || {}).searchStatus
                || SearchStatus.INITIAL) : (searchStatus || SearchStatus.INITIAL),
            searchObj: {
              ...FiltersInitialValue.hotel,
              ...(state.hotelSearches[tripId] || {}).searchObj,
              ...searchObj,
            },
            defaultSearchObj: {
              ...FiltersInitialValue.hotel,
              ...(state.hotelSearches[tripId] || {}).defaultSearchObj,
              ...defaultSearchObj,
            },
            selectedAddress: selectedAddress === null
              ? null
              : selectedAddress
                  || (state.hotelSearches[tripId] && state.hotelSearches[tripId].selectedAddress),
            searchProgress,
            requestId: requestId === undefined
              ? (state.hotelSearches[tripId] || {}).requestId : requestId,
          },
        },
      },
    }),
    setCurrentRateDetailCode: (state, { payload: { hotelKey, rateCode } }) => ({
      ...state,
      currentRateDetailCodes: {
        ...state.currentRateDetailCodes,
        [hotelKey]: rateCode,
      },
    }),
    setIsUserInteractedWithHotelList: (state, action) => ({
      ...state,
      hasUserInteractedWithHotelList: action && action.payload,
    }),
    hotelSearch: (state, action) => ({
      ...state,
      hasUserInteractedWithHotelList: action.payload.selectedAddress
        || (action.payload.searchObj
            && (action.payload.searchObj.checkOutDate
            || action.payload.searchObj.checkInDate
            || action.payload.searchObj.defaultSearchObj
            || action.payload.searchObj.doShowOnlyAgentPreferredHotels
            || action.payload.searchObj.doShowAvailableHotelsOnly
            || action.payload.searchObj.rateCodes
            || action.payload.searchObj.showRefundableHotelRatesOnly
            || action.payload.searchObj.hasFilterApplied)) ? false
        : state.hasUserInteractedWithHotelList,
    }),
    hotelSearchSetFoundTotalHotels: (state, {
      payload: {
        tripId,
        foundTotalHotels,
        searchProgress,
      },
    }) => ({
      ...state,
      hotelSearches: {
        ...state.hotelSearches,
        [tripId]: {
          ...state.hotelSearches[tripId],
          foundTotalHotels,
          searchProgress: searchProgress !== undefined
            ? searchProgress : state.hotelSearches[tripId].searchProgress,
        },
      },
    }),
    hotelSearchSuccess: (state, {
      payload: {
        queryString,
        response,
        tripId,
        amenitiesResults,
        hotelChainsResults,
        hotelResults,
        isSelectedAddress,
        minPrice,
        maxPrice,
        currencyCode,
      },
    }) => ({
      ...state,
      hotelSearchResponses: {
        ...state.hotelSearchResponses,
        [queryString]: {
          isSelectedAddress,
          ...response,
        },
      },
      hotelSearches: {
        ...state.hotelSearches,
        [tripId]: {
          ...state.hotelSearches[tripId],
          queryString,
          searchStatus: SearchStatus.DONE,
          amenitiesResults,
          hotelChainsResults,
          hotelResults,
          minPrice,
          maxPrice,
          currencyCode,
        },
      },
    }),
    hotelSearchFailure: (state, { payload }) => ({
      ...state,
      error: payload,
    }),
    deepLinkSearchRequest: (state) => ({
      ...state,
      deepLinkSearchStatus: SearchStatus.SEARCHING,
    }),
    deepLinkSearchSucess: (state) => ({
      ...state,
      deepLinkSearchStatus: SearchStatus.DONE,
    }),
    getHotelRequest: (state, {
      payload: {
        itinerary,
        shopKeyForAdditionalRooms,
      },
    }) => ({
      ...state,
      hasUserInteractedWithHotelList: true,
      hotelDetails: {
        ...state.hotelDetails,
        [itinerary.hotelKey]: shopKeyForAdditionalRooms ? {
          ...state.hotelDetails[itinerary.hotelKey],
          searchStatus: SearchStatus.SEARCHING,
        } : {
          searchStatus: SearchStatus.SEARCHING,
        },
      },
    }),
    getHotelSuccess: (state, {
      payload: {
        itinerary,
        response,
        searchKey,
        shopKeyForAdditionalRooms,
        doSearchHotelWithDeeplink,
      },
    }) => {
      if (shopKeyForAdditionalRooms) {
        const prevHotelDetails = state.hotelDetails[itinerary.hotelKey]
          ? current(state.hotelDetails[itinerary.hotelKey]).hotel : {};
        if (prevHotelDetails && prevHotelDetails.products
          && response && response.hotel && response.hotel.products) {
          let updatedProducts = [...prevHotelDetails.products, ...response.hotel.products];
          updatedProducts = _.orderBy(updatedProducts,
            [(p) => !p.isMatchWithRequestedRatePlanCode, (p) => p.roomPrice.originalPrice]);
          return {
            ...state,
            hotelDetails: {
              ...state.hotelDetails,
              [itinerary.hotelKey]: {
                ...state.hotelDetails[itinerary.hotelKey],
                hotel: {
                  ...state.hotelDetails[itinerary.hotelKey].hotel,
                  shopKeyForAdditionalRooms: response.hotel.shopKeyForAdditionalRooms,
                  products: updatedProducts,
                },
                searchStatus: SearchStatus.DONE,
              },
            },
            hotelDetailResponses: {
              ...state.hotelDetailResponses,
              [searchKey]: searchKey && state.hotelDetailResponses[searchKey] ? {
                ...state.hotelDetailResponses[searchKey],
                hotel: {
                  ...state.hotelDetailResponses[searchKey].hotel,
                  shopKeyForAdditionalRooms: response.hotel.shopKeyForAdditionalRooms,
                  products: updatedProducts,
                },
              } : undefined,
            },
          };
        }
        return {
          ...state,
          hotelDetails: {
            ...state.hotelDetails,
            [itinerary.hotelKey]: {
              ...state.hotelDetails[itinerary.hotelKey],
              qs: searchKey,
              searchStatus: SearchStatus.DONE,
            },
          },
        };
      }

      // With hotel details response, we don't have searchGeoname and distance info.
      // Assigning searchGeoname and distance info which returned with hotel search response
      const modifiedResponse = {
        ...response,
        searchGeoname: itinerary.searchGeoname,
        driveDistanceToCruisePort: itinerary.driveDistanceToCruisePort,
        driveDistanceToSearchDestination: itinerary.driveDistanceToSearchDestination,
        driveDurationToCruisePort: itinerary.driveDurationToCruisePort,
        driveDurationToSearchDestination: itinerary.driveDurationToSearchDestination,
        walkDistanceToSearchDestination: itinerary.walkDistanceToSearchDestination,
        hotel: {
          ...response.hotel,
        },
        qs: searchKey,
      };
      return {
        ...state,
        hotelDetails: {
          ...state.hotelDetails,
          [doSearchHotelWithDeeplink ? modifiedResponse.hotelKey
            : ((modifiedResponse && modifiedResponse.hotelKey)
            || itinerary.hotelKey)]: modifiedResponse,
        },
        hotelDetailResponses: {
          ...state.hotelDetailResponses,
          [searchKey]: searchKey ? {
            ...modifiedResponse,
          } : null,
        },
      };
    },
    getHotelFailure: (state, { payload: { itinerary, error, shopKeyForAdditionalRooms } }) => ({
      ...state,
      error,
      hotelDetails: {
        ...state.hotelDetails,
        [itinerary.hotelKey]: shopKeyForAdditionalRooms ? {
          ...state.hotelDetails[itinerary.hotelKey],
          searchStatus: SearchStatus.DONE,
        } : {},
      },
    }),
    clearSearch: () => initialState,
    getRoomDetailRequest: (state, {
      payload: {
        hotelProducts,
      },
    }) => {
      const productCodes = {};
      if (hotelProducts?.length) {
        _.each(hotelProducts,
          (p) => { productCodes[p.code] = SearchStatus.SEARCHING; });
      }
      return {
        ...state,
        getRoomDetailsStatus: {
          ...state.getRoomDetailsStatus,
          ...productCodes,
        },
      };
    },
    getRoomDetailResponse: (state, {
      payload: {
        hotelKey,
        data,
        searchKey,
        existingProduct,
      },
    }) => {
      const itinerary = state.hotelDetails[hotelKey];
      const productCodes = {};
      if (itinerary?.hotel?.products?.length > 0) {
        const existingProducts = itinerary.hotel.products
          .filter((x) => x.code === existingProduct?.code);
        existingProducts.forEach((product) => {
          productCodes[product.code] = SearchStatus.DONE;
        });
      }
      const modifiedHotel = Object.keys(data || {}).length > 0 ? {
        ...state.hotelDetails[itinerary.hotelKey],
        hotel: {
          ...state.hotelDetails[itinerary.hotelKey].hotel,
          products: [
            ...(_.map(state.hotelDetails[itinerary.hotelKey]
              && state.hotelDetails[itinerary.hotelKey].hotel
              && state.hotelDetails[itinerary.hotelKey].hotel.products, (p) => {
              if (data[p.code]) {
                const newData = { ...data[p.code] };
                newData.rooms = newData.rooms ? newData.rooms.map((room, roomIndex) => ({
                  ...room,
                  images: (p.rooms && p.rooms[roomIndex] && p.rooms[roomIndex].images
                     && p.rooms[roomIndex].images.length > 0)
                    ? p.rooms[roomIndex].images
                    : room.images,
                  originalDesc: p.rooms[roomIndex].originalDesc || p.rooms[roomIndex].desc,
                  originalFormattedDesc: p.rooms[roomIndex].originalFormattedDesc
                    || p.rooms[roomIndex].formattedDesc,
                })) : null;
                return {
                  ...p,
                  ...newData,
                  isMatchWithRequestedRatePlanCode: p.isMatchWithRequestedRatePlanCode,
                };
              } return { ...p };
            })),
          ],
        },
      } : {
        ...state.hotelDetails[itinerary.hotelKey],
      };
      return {
        ...state,
        hotelDetails: {
          ...state.hotelDetails,
          [itinerary.hotelKey]: modifiedHotel,
        },
        getRoomDetailsStatus: {
          ...state.getRoomDetailsStatus,
          ...productCodes,
        },
        hotelDetailResponses: {
          ...state.hotelDetailResponses,
          [searchKey]: modifiedHotel,
        },
      };
    },
    getRoomDescriptionRequest: (state, { payload: { code } }) => ({
      ...state, // TODO: add status
      roomDescriptions: {
        ...state.roomDescriptions,
        [code]: {
          ...state.roomDescriptions[code],
          status: SearchStatus.SEARCHING,
        },
      },
    }),
    getRoomDescriptionResponse: (state, { payload: { code, description } }) => ({
      ...state,
      roomDescriptions: {
        ...state.roomDescriptions,
        [code]: {
          status: SearchStatus.DONE,
          description,
        },
      },
    }),
    getHotelRateCodeKeysRequest: (state, { payload: { hotelRateDescription } }) => ({
      ...state,
      hotelRateDescription,
      hotelRateCodeKeysLoadingStatus: SearchStatus.SEARCHING,
    }),
    getHotelRateCodeKeysResponse: (state, { payload: { hotelRateDescription } }) => ({
      ...state,
      hotelRateDescription,
      hotelRateCodeKeysLoadingStatus: SearchStatus.DONE,
    }),
    getHotelRateCodeKeysFailure: (state) => ({
      ...state,
      hotelRateCodeKeysLoadingStatus: SearchStatus.DONE,
    }),
    setHotelDetailLoading: (state, action) => ({
      ...state,
      isHotelDetailLoading: action.payload,
    }),
    setHotelRateSearch: (state, action) => ({
      ...state,
      isHotelRateSearch: action.payload,
    }),
  },
});

export const {
  clearSearch,
  updateHotelSearchObj,
  setIsUserInteractedWithHotelList, hotelSearchSetFoundTotalHotels,
  hotelSearch, hotelSearchFailure, hotelSearchSuccess,
  getHotelRequest, deepLinkSearchRequest, deepLinkSearchSucess, getHotelSuccess, getHotelFailure,
  getRoomDetailRequest, getRoomDetailResponse,
  getRoomDescriptionRequest,
  getRoomDescriptionResponse,
  getHotelRateCodeKeysRequest,
  getHotelRateCodeKeysResponse,
  getHotelRateCodeKeysFailure,
  setHotelDetailLoading,
  setHotelRateSearch,
  setCurrentRateDetailCode,
} = hotelSlice.actions;
export default hotelSlice.reducer;
